import {
  TicketmasterShowsParams,
  fetchTicketmasterComedyShows,
} from "@/data/v2/client/ticketmaster";

import { ShowWithComedian } from "@/data/shows";
import dayjs from "@punchup/utils/dayjs";
import { useQuery } from "@tanstack/react-query";

// Create a stable cache key from parameters
function createTicketmasterCacheKey(params: TicketmasterShowsParams): string {
  const {
    city,
    stateCode,
    countryCode,
    point,
    radius,
    startDateTime,
    endDateTime,
  } = params;

  // Normalize parameters for consistent caching
  const normalizedParams = {
    city: city?.toLowerCase().trim(),
    stateCode: stateCode?.toUpperCase().trim() || "",
    countryCode: countryCode?.toUpperCase().trim() || "US",
    point: point ? `${point.latitude},${point.longitude}` : "",
    radius: radius || 25,
    // Round dates to the day to improve cache hit rates
    startDate: startDateTime ? dayjs(startDateTime).format("YYYY-MM-DD") : "",
    endDate: endDateTime ? dayjs(endDateTime).format("YYYY-MM-DD") : "",
  };

  return `ticketmaster-${normalizedParams.city}-${normalizedParams.stateCode}-${normalizedParams.countryCode}-${normalizedParams.point}-${normalizedParams.radius}-${normalizedParams.startDate}-${normalizedParams.endDate}`;
}

export interface UseTicketmasterShowsOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
}

export function useTicketmasterShows(
  params: TicketmasterShowsParams,
  options: UseTicketmasterShowsOptions = {}
) {
  const {
    enabled = true,
    staleTime = 1000 * 60 * 15, // 15 minutes
    cacheTime = 1000 * 60 * 30, // 30 minutes
  } = options;

  const cacheKey = createTicketmasterCacheKey(params);

  return useQuery<ShowWithComedian[], Error>({
    queryKey: ["ticketmaster-shows", cacheKey],
    queryFn: () => fetchTicketmasterComedyShows(params),
    enabled: enabled && !!params.city,
    staleTime,
    gcTime: cacheTime, // Updated from cacheTime to gcTime for TanStack Query v5
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook specifically for nearby shows integration
 * Pre-configured with optimized settings for the nearby shows use case
 */
export function useTicketmasterNearbyShows(
  params: TicketmasterShowsParams & {
    shouldFetch: boolean;
    maxShowsPerWeek?: number;
  }
) {
  const { shouldFetch, ...ticketmasterParams } = params;

  return useTicketmasterShows(ticketmasterParams, {
    enabled: shouldFetch,
    staleTime: 1000 * 60 * 10, // 10 minutes for nearby shows
    cacheTime: 1000 * 60 * 60, // 1 hour cache time
  });
}
